import type { TippyProps } from '@tippyjs/react';
import classnames from 'classnames';
import { useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import type { ALLOWED_SORTS } from '../thread/ThreadMessages/query';
import { sortKeyMapApiToFront } from '../thread/ThreadMessages/query';
import type { SortType } from '../types/layout';
import { warnOrThrow } from '../utils/common-utils2';
import { NoSSR } from '../utils/ssr';
import GradientBorderContainer from './GradientBorderContainer';
import { IconArrowDown } from './icons/Icons';
import SortIcon from './icons/SortIcon';
import { Menu } from './Menu';
import CustomRadioButton from './Radio/CustomRadioButton';

const messages = defineMessages({
  relevance: { defaultMessage: 'Relevance (BubbleFlow)', id: 'npqbn2' },
  newest: { defaultMessage: 'Most recent', id: 'fq86MK' },
  oldest: { defaultMessage: 'Older', id: 'iIeVwA' },
});

const messagesSelected = defineMessages({
  relevance: { defaultMessage: 'Sort by relevance', id: 'YQfAfT' },
  newest: { defaultMessage: 'Sort by most recent', id: '/fYg3J' },
  oldest: { defaultMessage: 'Sort by oldest', id: 'ueSHyf' },
});

export type SortMenuPropTypes = {
  options?: [string, MessageDescriptor][];
  sortType: SortType;
  changeSort: (elt: string) => void;
  isResponsive?: boolean;
  popoverPosition?: TippyProps['placement'];
};

/**
 *
 * @param options
 * @param sortType
 * @param changeSort
 * @constructor
 */
export const SortMenu = function SortMenu({
  options = Object.entries(messages),
  sortType,
  changeSort,
  isResponsive,
  popoverPosition,
}: SortMenuPropTypes) {
  const [menuOpen, setMenuOpen] = useState(false);

  let sortKey: ALLOWED_SORTS =
    sortType in sortKeyMapApiToFront ? sortKeyMapApiToFront[sortType] : sortType;

  if (!(sortKey in messagesSelected)) {
    warnOrThrow(`The sort key is invalid: ${sortKey}`);
    sortKey = 'relevance';
  }

  return (
    <NoSSR>
      <Menu
        onShow={() => setMenuOpen(true)}
        onHide={() => setMenuOpen(false)}
        content={options.map(([optionKey, message]) => (
          <button key={optionKey} type="button" onClick={() => changeSort(optionKey)}>
            <div className="flex items-center gap-4">
              <CustomRadioButton isSelected={sortType === optionKey} />
              <span className={classnames(sortType === optionKey && 'font-semibold')}>
                <FormattedMessage {...message} />
              </span>
            </div>
          </button>
        ))}
        className="border border-primary"
        {...(popoverPosition && { placement: popoverPosition })}
      >
        <button type="button">
          <GradientBorderContainer
            from={sortType === 'relevance' ? 'from-[#E96C0B]' : 'from-[#7D8EBF]'}
            via={sortType === 'relevance' ? 'via-[#E9990B]' : undefined}
            to={sortType === 'relevance' ? 'to-[#F6C919]' : 'to-[#8AB6E2]'}
            contentContainerClass="group px-2 md:px-3 hover:bg-transparent"
          >
            <div className="flex gap-x-3 items-center">
              <span className={classnames(menuOpen && 'text-primary')}>
                <SortIcon
                  sortType={sortType}
                  className="group-hover:[&_path]:fill-white group-hover:[&_rect]:fill-white"
                />
              </span>
              <span
                className={twMerge(
                  'hidden font-semibold text-[14px] text-gray-soft group-hover:text-white',
                  isResponsive && 'sm:flex',
                )}
              >
                <FormattedMessage {...messages[sortKey]} />
              </span>
              <IconArrowDown />
            </div>
          </GradientBorderContainer>
        </button>
      </Menu>
    </NoSSR>
  );
};
